<template>
  <div
    class="textarea"
    :class="classes"
  >
    <div class="textarea__content">
      <label
        :for="$props.inputId"
        class="textarea__label"
        @click="textareaRef.focus()"
        v-html="autoLabel"
      />
      <div
        ref="growWrapRef"
        class="grow-wrap body-2"
      >
        <textarea
          :id="$props.inputId"
          ref="textareaRef"
          v-model.trim="model"
          v-maska="$props.mask"
          class="textarea__input outline"
          v-bind="$attrs"
          :maxlength="$props.maxLength"
          :name="$props.name"
          @focus="handleFocus"
          @blur="handleBlur"
          @input="setInputOnWrapper"
        />
      </div>
      <div class="textarea__indicator" />
    </div>
    <div class="textarea__info">
      <div class="textarea__supporting-text caption-regular">
        <label
          :for="$props.inputId"
          v-text="autoHint"
        />
      </div>
      <div
        class="caption-regular"
        v-text="`${valueLength}/${$props.maxLength}`"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, useId } from 'vue'
  import { vMaska } from 'maska/vue'

  import useI18n from '@shared/hooks/useI18n'

  // HOOKS
  const { t } = useI18n()

  // INIT
  const model = defineModel({ type: [String, Number] })
  const props = defineProps({
    hasIconValidIndicator: {
      type: Boolean,
      default: false,
    },

    inputId: {
      type: String,
      default: () => useId()
    },

    label: {
      type: String,
      default: null,
    },

    manualValidation: {
      type: Boolean,
      default: false,
    },

    mask: {
      type: [String, Array, Boolean, Object],
      default: false,
    },

    maxLength: {
      type: Number,
      default: 1000,
    },

    name: {
      type: String,
      required: true,
    },

    size: {
      validator(value) {
        return ['sm', 'md'].includes(value)
      },
      default: 'sm',
    },

    supportingText: {
      type: String,
      default: undefined,
    },

    v: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const isFocused = ref(false)
  const textareaRef = ref()
  const growWrapRef = ref(null)

  // COMPUTED
  const classes = computed(() => {
    return {
      [`textarea--${props.size}`]: true,
      'textarea--focus': isFocused.value,
      'textarea--empty': isEmpty.value,
      'textarea--success': props.hasIconValidIndicator && isValid.value,
      'textarea--error': isInvalid.value,
    }
  })

  const autoLabel = computed(() => {
    let str = props.label
    if (isRequired.value) {
      str += ' *'
    }

    return str
  })

  const autoHint = computed(() => {
    if (isInvalid.value) {
      const error = props.v?.$errors[0]?.$validator
      if (error) return t(`form.error.${error}`)
    }
    return props.supportingText
  })

  const isEmpty = computed(() => {
    return model.value === undefined || model.value === null || model.value.length === 0
  })

  const isInvalid = computed(() => {
    return props.v?.$dirty && props.v?.$invalid
  })

  const isRequired = computed(() => {
    return !!props.v?.required
  })

  const isValid = computed(() => {
    return props.v?.$dirty && !props.v?.$invalid
  })

  const valueLength = computed(() => {
    return model.value?.length ?? 0
  })

  // METHODS
  function handleBlur() {
    textareaRef.value?.classList.remove('has-outline')

    if (!props.manualValidation) props.v?.$validate()
    isFocused.value = false
  }

  function handleFocus() {
    isFocused.value = true
  }

  function setInputOnWrapper(event) {
    growWrapRef.value.dataset.replicatedValue = event.target.value
  }

  // LIFECYCLE HOOKS
  onMounted(() => {
    growWrapRef.value.dataset.replicatedValue = model.value
  })

  defineExpose({
    handleBlur,
  })
</script>

<style>
:root,
.surface-default {
  --Input-fields-input-on-alternative-surface-fill: var(--surface);
  --Input-fields-input-on-default-surface-fill: var(--surface-ext-a);
  --Input-fields-input-on-Inverted-surface-fill: var(--primary-container-low);
  --Input-fields-input-on-Inverted-surface-active-indicator: var(--on-inverse-surface);
  --Input-fields-input-border-radius: var(--radius-border-radius-02);
  --Input-fields-input-active-indicator-spacing: var(--fixed-spacing-fix-04);

  --dvp-textarea-c-background: var(--Input-fields-input-on-default-surface-fill);
  --dvp-textarea-c-default-active-indicator: var(--on-surface);

  --dvp-textarea-c-background-disabled: var(--Interaction-States-disabled-default);
  --dvp-textarea-c-label-disabled: var(--on-surface);

  --dvp-textarea-c-outline: var(--on-surface);
}

.surface-alternative {
  --dvp-textarea-c-background: var(--Input-fields-input-on-alternative-surface-fill);
  --dvp-textarea-c-background-disabled: var(--Interaction-States-disabled-extended);
  --dvp-textarea-c-label-disabled: var(--on-surface);
  --dvp-textarea-c-outline: var(--on-surface);
}

.surface-inverted {
  --dvp-textarea-c-background: var(--Input-fields-input-on-Inverted-surface-fill);
  --dvp-textarea-c-default-active-indicator: var(--Input-fields-input-on-Inverted-surface-active-indicator);
  --dvp-textarea-c-background-disabled: var(--Interaction-States-disabled-inverse);
  --dvp-textarea-c-label-disabled: var(--Interaction-States-disabled-inverse-label);
  --dvp-textarea-c-outline: var(--on-inverse-surface-variant);
}
</style>

<style scoped>
.textarea {
  --dvp-textarea-c-label-text: var(--on-surface-medium);
  --dvp-textarea-c-active-indicator: var(--dvp-textarea-c-default-active-indicator);
  --dvp-textarea-c-supporting-text: var(--on-surface-medium);

  display: inline-block;
  width: 100%;
  margin: 0 0 var(--fixed-spacing-fix-04);
}

/* Valid input */
.textarea--success {
  --dvp-textarea-c-label-text: var(--on-surface-medium);
  --dvp-textarea-c-active-indicator: var(--secondary);
  --dvp-textarea-c-supporting-text: var(--on-surface-medium);
}

/* Input error */
.textarea--error {
  --dvp-textarea-c-label-text: var(--error);
  --dvp-textarea-c-active-indicator: var(--error);
  --dvp-textarea-c-supporting-text: var(--error);
}

/* Small sized input */
.textarea,
.textarea--sm {
  --input-height: var(--spacing-san-spacing-30);
  --input-active-indicator-height: 2px;
  --input-label-top-spacing: 11px;
}

/* Medium sized input */
.textarea--md {
  --input-label-top-spacing: 6px;
}

/* Disabled input */
.textarea:has(.textarea__input[disabled]) {
  pointer-events: none;
}

.textarea:has(.textarea__input[disabled]) .textarea__content {
  background-color: var(--dvp-textarea-c-background-disabled);
}

.textarea:has(.textarea__input[disabled]) .textarea__label,
.textarea:has(.textarea__input[disabled]) .textarea__input,
.textarea:has(.textarea__input[disabled]) .textarea__supporting-text {
  color: var(--dvp-textarea-c-label-disabled);
}

/* Active (focused) input */
.textarea--focus {
  .textarea__indicator {
    height: 2px;
  }
}

/* Active (focused) input without success or error state */
.textarea--focus:not(.textarea--success):not(.textarea--error) {
  --dvp-textarea-c-label-text: var(--on-surface);
}

/* Focused input (also triggers when user clicks into input-field) */
.textarea:has(.has-outline):has(.textarea__input:focus-visible) .textarea__content {
  outline: 2px solid var(--dvp-textarea-c-outline);
  outline-offset: var(--fixed-spacing-fix-01);
  border-radius: var(--fixed-border-radius-fix-02);
}

/* Set input text size and label size (if input empty, not focused and there's no placholder) */
.textarea--sm .textarea__input,
.textarea--sm.textarea--empty:not(.textarea--focus)
  .textarea__content:has(.textarea__input:not(:placeholder-shown))
  > .textarea__label {
  font-size: var(--font-size-regular-md);
  letter-spacing: var(--letter-spacing-regular-md);
  line-height: var(--line-height-regular-md);
}

.textarea--md .textarea__input,
.textarea--md.textarea--empty:not(.textarea--focus)
  .textarea__content:has(.textarea__input:not(:placeholder-shown))
  > .textarea__label {
  font-size: var(--font-size-regular-xl);
  letter-spacing: var(--letter-spacing-regular-xl);
  line-height: var(--line-height-regular-xl);
}

/* Vertically position label (if input empty, not focused and there's no placeholder) */
.textarea--empty:not(.textarea--focus)
  .textarea__content:has(.textarea__input:not(:placeholder-shown))
  > .textarea__label {
  top: var(--input-label-top-spacing);
}

/*
.textarea--empty:not(.textarea--focus) .input__content:has(.textarea__input:not(:placeholder-shown)) .textarea__input {
  opacity: 0;
}
*/

.textarea__content {
  position: relative;
  background-color: var(--dvp-textarea-c-background);
  width: 100%;
  padding: var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-04) var(--fixed-spacing-fix-02);
  border-radius: var(--fixed-border-radius-fix-02);
}

.textarea__indicator {
  position: absolute;
  bottom: 1px;
  left: var(--fixed-spacing-fix-04);
  right: var(--fixed-spacing-fix-04);
  height: 1px;
  background-color: var(--dvp-textarea-c-active-indicator);
  border-radius: var(--fixed-border-radius-fix-02);
}

.textarea__label {
  position: absolute;
  top: 0;
  left: var(--fixed-spacing-fix-04);
  width: 100%;
  color: var(--dvp-textarea-c-label-text);
  font-size: var(--font-size-regular-xs);
  line-height: var(--line-height-regular-xs);
  letter-spacing: var(--letter-spacing-regular-xs);
  transition-property: all;
  transition-timing-function: ease-in-out;
  transition-duration: 200ms;
  cursor: text;
}

.textarea__input,
.grow-wrap::after {
  display: inline-block;
  overflow: auto;
  scrollbar-width: thin;
  width: 100%;
  border: none;
  font: inherit;
  background-color: inherit;
  opacity: 1;
  transition-property: opacity;
  transition-timing-function: ease-in-out;
  transition-duration: 200ms;
  color: var(--on-surface);

  /* Place on top of each other */
  grid-area: 1 / 1 / 2 / 2;
}

.textarea__info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  column-gap: var(--fixed-spacing-fix-04);
  padding: 0 var(--fixed-spacing-fix-04);
}

.textarea__supporting-text {
  --input-indent-supporting-text: var(--fixed-spacing-fix-04);

  flex-grow: 1;
  color: var(--dvp-textarea-c-supporting-text);
  height: var(--line-height-regular-xs);

  & label {
    display: inline-block;
    width: calc(100% - (2 * var(--input-indent-supporting-text)));
    white-space: nowrap;
    overflow: auto;
    scrollbar-width: thin;
    text-overflow: ellipsis;
  }
}

.grow-wrap {
  display: grid;
}

.grow-wrap::after {
  content: attr(data-replicated-value) ' ';
  white-space: pre-wrap;
  visibility: hidden;
}

.grow-wrap > .textarea__input {
  resize: none;
  overflow: auto;
  scrollbar-width: thin;
}
</style>
